import React, { useEffect, useMemo } from 'react';

import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';

import CardDialog from '../CardDialog';
import SkeletonTable from '../core/Skeletons/SkeletonTable';
import { useCms } from '../hooks/useCms';
import useOrganization from '../OrganizationContext';
import { useCliveConfigOptions } from '../SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import type { UserModel } from '../types/user-types';

import { useCommunicationsTab } from './hooks/useCommunicationsTab';

interface AdjusterCommunicationsV3Props {
  user: UserModel;
}

const AdjusterCommunicationsV3Wrapper: React.FC = () => {
  const { user, userOrganization } = useCms();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error useOrganization() not properly typed yet
  const { isLoadingCommunicationConfiguration } = useOrganization();

  // The communication configuration decides whether the handled-status filter opens on Pending, so
  // mounting the inner component before it resolves would fire an unfiltered adjuster-wide ids
  // request (up to 1000 ids) and its page-data request, then discard both and refetch with
  // handled_status=pending. Holding the mount here keeps it to one round trip.
  if (!user || !userOrganization || isLoadingCommunicationConfiguration) {
    return (
      <div className="space-y-8">
        <CardDialog title="Communications">
          <SkeletonTable rowsCount={10} columnsCount={5} maxHeight="45vh" isError={false} />
        </CardDialog>
      </div>
    );
  }

  return <AdjusterCommunicationsV3 user={user} />;
};

const AdjusterCommunicationsV3: React.FC<AdjusterCommunicationsV3Props> = ({ user }) => {
  const { setPageTitle } = useCms();
  const { isCliveEnabledForOrganization } = useCliveConfigOptions();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error useOrganization() not properly typed yet
  const { organizationContactRolesDict, communicationConfiguration, isLoadingCommunicationConfiguration } =
    useOrganization();

  useEffect(() => {
    setPageTitle('Adjuster Communications');
  }, [setPageTitle]);

  const showClive = useMemo(() => {
    return isCliveEnabledForOrganization();
  }, [isCliveEnabledForOrganization]);

  const { tabs, handleTabClick } = useCommunicationsTab({
    organizationId: user?.organization_id,
    userId: user?.id,
    user,
    communicationsPerPage: 20,
    removeExposuresColumn: true,
    removeAdjusterColumn: true,
    addClaimColumn: true,
    displayAdjusterFilter: false,
    displayExposuresFilter: false,
    displayHandledStatusFilter: true,
    isHandledStatusEnabled: communicationConfiguration?.is_handled_status_enabled === true,
    handledFilterDefaultsToPending:
      communicationConfiguration?.adjuster_page_handled_filter_defaults_to_pending === true,
    isCommunicationConfigLoading: Boolean(isLoadingCommunicationConfiguration),
    organizationContactRolesDict,
    showClive,
  });

  return (
    <div className="space-y-8">
      <CardDialog title="Communications">
        <InnerTabs tabs={tabs} onTabClick={handleTabClick} />
      </CardDialog>
    </div>
  );
};

export default AdjusterCommunicationsV3Wrapper;
